import { useState, useMemo } from 'react';
import { demoRooms } from '@/mocks/demoData';
import { useApp } from '@/contexts/AppContext';

interface SeasonalRate {
  id: string;
  room_category: string;
  season_name: string;
  start_date: string;
  end_date: string;
  weekday_rate: number;
  weekend_rate: number;
  active: boolean;
}

const mockRates: SeasonalRate[] = [
  { id: 'sr-001', room_category: 'Standard AC', season_name: 'Regular', start_date: '2026-01-01', end_date: '2026-12-31', weekday_rate: 1800, weekend_rate: 2200, active: true },
  { id: 'sr-002', room_category: 'Deluxe AC', season_name: 'Regular', start_date: '2026-01-01', end_date: '2026-12-31', weekday_rate: 2500, weekend_rate: 3000, active: true },
  { id: 'sr-003', room_category: 'Suite', season_name: 'Regular', start_date: '2026-01-01', end_date: '2026-12-31', weekday_rate: 4500, weekend_rate: 5200, active: true },
  { id: 'sr-004', room_category: 'Standard Non-AC', season_name: 'Regular', start_date: '2026-01-01', end_date: '2026-12-31', weekday_rate: 1200, weekend_rate: 1500, active: true },
  { id: 'sr-005', room_category: 'Deluxe AC', season_name: 'Diwali Peak', start_date: '2026-10-15', end_date: '2026-11-05', weekday_rate: 3800, weekend_rate: 4500, active: true },
  { id: 'sr-006', room_category: 'Suite', season_name: 'Diwali Peak', start_date: '2026-10-15', end_date: '2026-11-05', weekday_rate: 6500, weekend_rate: 7500, active: true },
  { id: 'sr-007', room_category: 'Standard AC', season_name: 'Summer Peak', start_date: '2026-04-01', end_date: '2026-06-30', weekday_rate: 2200, weekend_rate: 2600, active: true },
  { id: 'sr-008', room_category: 'Deluxe AC', season_name: 'Summer Peak', start_date: '2026-04-01', end_date: '2026-06-30', weekday_rate: 3000, weekend_rate: 3500, active: true },
];

const categories = [...new Set(demoRooms.map((r) => r.category))];

export default function RateCalendar() {
  const { addToast } = useApp();
  const [rates, setRates] = useState<SeasonalRate[]>(mockRates);
  const [showAdd, setShowAdd] = useState(false);
  const [newRate, setNewRate] = useState({ room_category: '', season_name: '', start_date: '', end_date: '', weekday_rate: '', weekend_rate: '' });
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [viewMonth, setViewMonth] = useState('2026-06');

  const filtered = useMemo(() => {
    return rates
      .filter((r) => selectedCategory === 'all' || r.room_category === selectedCategory)
      .filter((r) => r.start_date.startsWith(viewMonth) || r.end_date.startsWith(viewMonth) || (r.start_date < viewMonth && r.end_date > viewMonth));
  }, [rates, selectedCategory, viewMonth]);

  const handleAddRate = () => {
    if (!newRate.room_category || !newRate.season_name || !newRate.start_date || !newRate.end_date || !newRate.weekday_rate) {
      addToast('error', 'Fill all required fields');
      return;
    }
    const rate: SeasonalRate = {
      id: `sr-${Date.now()}`,
      room_category: newRate.room_category,
      season_name: newRate.season_name,
      start_date: newRate.start_date,
      end_date: newRate.end_date,
      weekday_rate: Number(newRate.weekday_rate),
      weekend_rate: Number(newRate.weekend_rate) || Number(newRate.weekday_rate),
      active: true,
    };
    setRates([rate, ...rates]);
    addToast('success', `Rate added: ${rate.season_name} — ${rate.room_category}`);
    setShowAdd(false);
    setNewRate({ room_category: '', season_name: '', start_date: '', end_date: '', weekday_rate: '', weekend_rate: '' });
  };

  const toggleActive = (id: string) => {
    setRates(rates.map((r) => (r.id === id ? { ...r, active: !r.active } : r)));
    const rate = rates.find((r) => r.id === id);
    addToast('info', `Rate ${rate?.active ? 'disabled' : 'enabled'} for ${rate?.season_name}`);
  };

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-semibold text-navy-800">Rate Calendar</h1>
          <p className="text-navy-500 mt-1">Manage seasonal pricing and special event rates for all room categories.</p>
        </div>
        <button onClick={() => setShowAdd(true)} className="px-4 py-2.5 bg-navy-700 text-white rounded-lg text-sm font-medium hover:bg-navy-800 transition-colors cursor-pointer whitespace-nowrap">
          <i className="ri-add-line mr-1" /> Add Season Rate
        </button>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl border border-navy-100 p-4 mb-6">
        <div className="flex flex-wrap gap-3 items-end">
          <div>
            <label className="block text-xs font-medium text-navy-500 mb-1.5">Category</label>
            <select value={selectedCategory} onChange={(e) => setSelectedCategory(e.target.value)} className="px-3 py-2.5 border border-navy-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-navy-500">
              <option value="all">All Categories</option>
              {categories.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-navy-500 mb-1.5">Month</label>
            <input type="month" value={viewMonth} onChange={(e) => setViewMonth(e.target.value)} className="px-3 py-2.5 border border-navy-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-navy-500" />
          </div>
        </div>
      </div>

      {/* Rates Table */}
      <div className="bg-white rounded-xl border border-navy-100 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-navy-50">
              <tr>
                <th className="text-left px-5 py-3 font-semibold text-navy-700">Season</th>
                <th className="text-left px-5 py-3 font-semibold text-navy-700">Category</th>
                <th className="text-left px-5 py-3 font-semibold text-navy-700">Period</th>
                <th className="text-right px-5 py-3 font-semibold text-navy-700">Weekday Rate</th>
                <th className="text-right px-5 py-3 font-semibold text-navy-700">Weekend Rate</th>
                <th className="text-center px-5 py-3 font-semibold text-navy-700">Active</th>
                <th className="text-center px-5 py-3 font-semibold text-navy-700">Action</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((r) => (
                <tr key={r.id} className={`border-t border-navy-100 hover:bg-navy-50/50 transition-colors ${!r.active ? 'opacity-50' : ''}`}>
                  <td className="px-5 py-3.5">
                    <span className="font-medium text-navy-800">{r.season_name}</span>
                  </td>
                  <td className="px-5 py-3.5 text-navy-600">{r.room_category}</td>
                  <td className="px-5 py-3.5 text-navy-500 text-xs">
                    {new Date(r.start_date).toLocaleDateString('en-IN')} — {new Date(r.end_date).toLocaleDateString('en-IN')}
                  </td>
                  <td className="px-5 py-3.5 text-right font-medium text-navy-800">Rs. {r.weekday_rate.toLocaleString('en-IN')}</td>
                  <td className="px-5 py-3.5 text-right font-medium text-gold-600">Rs. {r.weekend_rate.toLocaleString('en-IN')}</td>
                  <td className="px-5 py-3.5 text-center">
                    <button onClick={() => toggleActive(r.id)} className={`w-10 h-6 rounded-full transition-colors relative cursor-pointer ${r.active ? 'bg-green-500' : 'bg-navy-300'}`}>
                      <span className={`absolute top-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform ${r.active ? 'translate-x-4' : 'translate-x-0.5'}`} />
                    </button>
                  </td>
                  <td className="px-5 py-3.5 text-center">
                    <button onClick={() => { setRates(rates.filter((x) => x.id !== r.id)); addToast('info', `Rate removed: ${r.season_name}`); }} className="text-red-400 hover:text-red-600 cursor-pointer">
                      <i className="ri-delete-bin-line" />
                    </button>
                  </td>
                </tr>
              ))}
              {filtered.length === 0 && (
                <tr><td colSpan={7} className="px-5 py-12 text-center text-navy-400">No seasonal rates found for this month.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Default Tariffs */}
      <div className="mt-8">
        <h2 className="text-lg font-semibold text-navy-800 mb-4">Default Room Tariffs</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {categories.map((cat) => {
            const rooms = demoRooms.filter((r) => r.category === cat);
            const defaultRate = rooms[0]?.tariff || 0;
            const activeSeason = rates.find((r) => r.room_category === cat && r.active && new Date(r.start_date) <= new Date() && new Date(r.end_date) >= new Date());
            return (
              <div key={cat} className="bg-white rounded-xl border border-navy-100 p-4">
                <div className="text-xs text-navy-400 mb-1">{cat}</div>
                <div className="text-xl font-bold text-navy-800">Rs. {defaultRate.toLocaleString('en-IN')}</div>
                <div className="text-xs text-navy-500 mt-1">{rooms.length} rooms</div>
                {activeSeason && (
                  <div className="mt-2 px-2 py-1 bg-gold-50 border border-gold-200 rounded text-xs text-gold-700">
                    <i className="ri-sun-line mr-1" /> {activeSeason.season_name}: Rs. {activeSeason.weekday_rate.toLocaleString('en-IN')}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Add Rate Modal */}
      {showAdd && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md mx-4 overflow-hidden">
            <div className="px-6 py-4 border-b border-navy-100 flex items-center justify-between">
              <h3 className="font-semibold text-navy-800">Add Seasonal Rate</h3>
              <button onClick={() => setShowAdd(false)} className="text-navy-400 hover:text-navy-600 cursor-pointer"><i className="ri-close-line text-xl" /></button>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-navy-700 mb-1.5">Season Name *</label>
                <input type="text" value={newRate.season_name} onChange={(e) => setNewRate({ ...newRate, season_name: e.target.value })} className="w-full px-3 py-2.5 border border-navy-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-navy-500" placeholder="e.g. Diwali Peak, Summer Special" />
              </div>
              <div>
                <label className="block text-sm font-medium text-navy-700 mb-1.5">Room Category *</label>
                <select value={newRate.room_category} onChange={(e) => setNewRate({ ...newRate, room_category: e.target.value })} className="w-full px-3 py-2.5 border border-navy-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-navy-500">
                  <option value="">Select...</option>
                  {categories.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-navy-700 mb-1.5">Start Date *</label>
                  <input type="date" value={newRate.start_date} onChange={(e) => setNewRate({ ...newRate, start_date: e.target.value })} className="w-full px-3 py-2.5 border border-navy-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-navy-500" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-navy-700 mb-1.5">End Date *</label>
                  <input type="date" value={newRate.end_date} onChange={(e) => setNewRate({ ...newRate, end_date: e.target.value })} className="w-full px-3 py-2.5 border border-navy-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-navy-500" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-navy-700 mb-1.5">Weekday Rate (Rs.) *</label>
                  <input type="number" value={newRate.weekday_rate} onChange={(e) => setNewRate({ ...newRate, weekday_rate: e.target.value })} className="w-full px-3 py-2.5 border border-navy-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-navy-500" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-navy-700 mb-1.5">Weekend Rate (Rs.)</label>
                  <input type="number" value={newRate.weekend_rate} onChange={(e) => setNewRate({ ...newRate, weekend_rate: e.target.value })} className="w-full px-3 py-2.5 border border-navy-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-navy-500" />
                </div>
              </div>
            </div>
            <div className="px-6 py-4 border-t border-navy-100 flex gap-3">
              <button onClick={() => setShowAdd(false)} className="flex-1 px-4 py-2.5 border border-navy-200 rounded-lg text-sm font-medium text-navy-600 hover:bg-navy-50 transition-colors cursor-pointer">Cancel</button>
              <button onClick={handleAddRate} className="flex-1 px-4 py-2.5 bg-navy-700 text-white rounded-lg text-sm font-medium hover:bg-navy-800 transition-colors cursor-pointer">Add Rate</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}